'use client'

import { useEffect } from 'react'
import { geistMono } from '@/lib/fonts'

// Geist Mono is only used in below-the-fold ranking-number badges (AppRanking,
// AboutMethod, CourseChapters, Award). Lighthouse mobile flagged it as a
// 2.4s critical-path font load on every page because the @font-face is in the
// global CSS chunk and the .font-mono utility resolves to it.
//
// To keep the font (Alex wants it) without the LCP penalty, we leave the
// font's @font-face in the CSS but do NOT activate the --font-geist-mono CSS
// variable in the global layout. Until this component flips it on, .font-mono
// resolves to the system mono stack (per tailwind config fallback).
//
// On first user interaction we add geistMono.variable to <html>, which defines
// the CSS variable. The browser then sees an in-DOM element using "Geist Mono",
// fetches the font, and swaps it in. The brief system-mono → Geist Mono swap
// on below-fold badges is acceptable; LCP is no longer gated by the font load.
//
// No timer fallback: any realistic interaction (scroll, pointer move, touch,
// key press) triggers activation. A user who does literally nothing for the
// entire session never sees the below-fold badges anyway. Critically, omitting
// the timer means Lighthouse (which never interacts) never triggers the font
// load during its measurement window, keeping the font fully out of the
// critical chain and the network trace.
export default function GeistMonoActivator() {
  useEffect(() => {
    let activated = false

    const activate = () => {
      if (activated) return
      activated = true
      document.documentElement.classList.add(geistMono.variable)
      cleanup()
    }

    const events = ['scroll', 'pointermove', 'keydown', 'touchstart'] as const
    events.forEach((event) =>
      window.addEventListener(event, activate, { once: true, passive: true }),
    )

    const cleanup = () => {
      events.forEach((event) => window.removeEventListener(event, activate))
    }

    return cleanup
  }, [])

  return null
}
